/**
 * @file src/room/roomGrant.js
 * @description The room grant carried on the boot URL, plus the one header helper the fetch layer
 * uses to present it.
 *
 * A multiplayer boot opens with `?grant=<grantId>`: a session-scoped authorization the backend
 * mints once (validating the anchor's access to the whole roster) and honors on the entity/release
 * load endpoints in place of each viewer's own permissions — so every participant loads the whole
 * roster once, with zero per-load permission checks. Permissions live in the backend; the client
 * only presents the grant.
 *
 * No grant param → the header helper returns an empty bag → loads behave exactly as before.
 */

const HEADER_NAME = 'x-room-grant';

// Memoized on the query string: the boot URL doesn't change under a session, and the fetch layer
// reads this on every gated load.
let cache = { search: null, grantId: null };

/**
 * Grant ids are opaque, base64url-ish session tokens. Keep only header-safe token characters: a
 * junk `?grant=` value can never inject a header, and an accidental bad param can't throw when the
 * value is set on a `Headers` instance.
 */
function sanitizeGrantId(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed || !/^[A-Za-z0-9._~+/=-]+$/.test(trimmed)) return null;
  return trimmed;
}

function readGrantId() {
  if (typeof window === 'undefined') return null;
  const search = window.location?.search ?? '';
  if (cache.search === search) return cache.grantId;
  let grantId = null;
  try {
    grantId = sanitizeGrantId(new URLSearchParams(search).get('grant'));
  } catch {
    grantId = null;
  }
  cache = { search, grantId };
  return grantId;
}

/** The room grant id from the boot URL, or null outside a room-grant boot. */
export function getRoomGrantId() {
  return readGrantId();
}

/**
 * Header bag presenting the room grant on a gated entity/release load — empty (`{}`) when there is
 * no grant on the URL, so a non-room load stays byte-identical.
 * @returns {Record<string, string>}
 */
export function getRoomGrantHeaders() {
  const grantId = readGrantId();
  return grantId ? { [HEADER_NAME]: grantId } : {};
}

/** Test-only: drop the memo so a test can exercise a fresh window/query string. */
export function resetRoomGrantCache() {
  cache = { search: null, grantId: null };
}

/**
 * Did matchmaking send us here?
 *
 * Quick play boots with `?match=1`. It is the one thing that distinguishes "I was
 * sent here to play" from "I followed a link and am happy to listen", and the
 * relay needs it to know whether a missing seat is a refusal or an ordinary
 * spectator arrival.
 *
 * Read from the URL rather than passed down, for the same reason the grant is:
 * this is a property of how the tab was opened, and every part of the boot path
 * that needs it can ask without threading it through half a dozen constructors.
 */
export function isMatchmakingArrival() {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('match') === '1';
  } catch {
    return false;
  }
}
